import type { Big } from 'big.js';
import { Side } from '@/common/constants.ts';
import type { MarketData } from '@/common/MarketData.ts';
import { addBig, divideBig, multiplyBig, stringToBig, subtractBig } from '@/utils/bigUtils.ts';
import { useOrderBookStore } from './orderBookStore.ts';

function bestPriceForSide(side: Side, market: MarketData): number | undefined {
  const book = useOrderBookStore.getState();
  if (market.id == null || book.symbol !== String(market.id)) return undefined;
  return side === Side.long ? book.bestAskPrice : book.bestBidPrice;
}

// Measured from this side's BEST price, matching how the exchange applies the API path's `ms` cap,
// so an on-chain order stops at the same level. The mid/mark fallback is stricter on both sides.
export function calculateMarketPriceWithSlippage(side: Side, market: MarketData, slippagePercentOverride?: number): Big {
  const basePrice = bestPriceForSide(side, market) ?? market.midPrice ?? market.markPrice;
  const basePriceBig = stringToBig(basePrice);
  const slippagePercent = slippagePercentOverride || market.orderMaxImpactP || 0;
  const impactMultiplier = divideBig(slippagePercent, 100);
  const priceImpact = multiplyBig(basePriceBig, impactMultiplier);

  // For long orders (buying), we accept higher price (basePrice + impact)
  // For short orders (selling), we accept lower price (basePrice - impact)
  return side === Side.long ? addBig(basePriceBig, priceImpact) : subtractBig(basePriceBig, priceImpact);
}
